/**
 * OutletMappingPage — Phase R10.4c (2026-07-05)
 *
 * Owner-facing "Outlet Mapping" master page with 3 tabs:
 *   - Unmapped Outlets  — aggregated detected outlets lacking a branch
 *   - Saved Mappings    — list/search/edit/toggle saved mappings
 *   - Mapping History   — audit timeline for a selected mapping
 *
 * Loaded at /owner/outlet-mapping, visible to brand_owner + super_admin.
 */
import { useState } from 'react';
import { Store, History as HistoryIcon, ListChecks } from 'lucide-react';
import { PageHeader } from '@/components/ui_primitives/Misc';
import UnmappedOutletsTab from '@/components/owner/OutletMapping/UnmappedOutletsTab';
import SavedMappingsTab   from '@/components/owner/OutletMapping/SavedMappingsTab';
import MappingHistoryTab  from '@/components/owner/OutletMapping/MappingHistoryTab';

const TABS = [
  { key: 'unmapped', label: 'Unmapped Outlets', Icon: Store },
  { key: 'saved',    label: 'Saved Mappings',   Icon: ListChecks },
  { key: 'history',  label: 'History',          Icon: HistoryIcon },
];

export default function OutletMappingPage() {
  const [tab, setTab] = useState('unmapped');
  const [historyMapping, setHistoryMapping] = useState(null);

  const activeTab = historyMapping ? 'history' : tab;

  return (
    <div className="space-y-6" data-testid="owner-outlet-mapping-page">
      <PageHeader
        kicker="R10.4c"
        title="Outlet Mapping"
        subtitle="Teach OneStamp how OCR-detected outlets map to your branches. Future receipts auto-resolve."
      />

      {/* Tabs */}
      <div
        className="flex items-center gap-2 border-b border-white/10 pb-1 overflow-x-auto"
        data-testid="outlet-mapping-tabs"
      >
        {TABS.map(({ key, label, Icon }) => {
          const disabled = key === 'history' && !historyMapping;
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (key === 'history') {
                  if (!historyMapping) return;
                } else {
                  setHistoryMapping(null);
                }
                setTab(key);
              }}
              data-testid={`outlet-mapping-tab-${key}`}
              className={
                'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-xl -mb-px border-b-2 transition-colors ' +
                (isActive
                  ? 'text-gold border-gold'
                  : 'text-white/55 border-transparent hover:text-white/85') +
                (disabled ? ' opacity-40 cursor-not-allowed' : '')
              }
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </div>

      {activeTab === 'unmapped' ? <UnmappedOutletsTab /> : null}
      {activeTab === 'saved' ? (
        <SavedMappingsTab
          onViewHistory={(row) => {
            setHistoryMapping(row);
            setTab('history');
          }}
        />
      ) : null}
      {activeTab === 'history' && historyMapping ? (
        <MappingHistoryTab
          mapping={historyMapping}
          onBack={() => {
            setHistoryMapping(null);
            setTab('saved');
          }}
        />
      ) : null}
    </div>
  );
}
